// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import * as React from "react";

interface OwnProps extends WidgetProps {
    fields: {
        title: string;
        cardWidth: "100%" | "50%" | "33%" | "25%";
        counters: {
            fields: {
                heading: number;
                subHeading: string;
            };
        }[];
    };
}

type Props = OwnProps;

export interface CounterWidgetStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    counter?: InjectableCss;
    title?: TypographyProps;
    heading?: TypographyProps;
    subHeading?: TypographyProps;
}

export const counterWidgetStyles: CounterWidgetStyles = {};

const counterWidget: React.FC<Props> = ({ id, fields }) => {
    const styles = counterWidgetStyles;
    const width = fields.cardWidth;
    const counters: JSX.Element[] = [];
    fields.counters?.forEach((counter, index) =>
        counters.push(
            <StyledWrapper key={`${id}-${index}`} {...styles.counter} {...{width}}>
                <Typography {...styles.heading}>{counter.fields.heading}</Typography>
                <Typography {...styles.subHeading}>{counter.fields.subHeading}</Typography>
            </StyledWrapper>,
        ),
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.title && <Typography {...styles.title}>{fields.title}</Typography>}
            <StyledWrapper {...styles.container}>{counters}</StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: counterWidget,
    definition: {
        displayName: "Counter Widget",
        group: XNGAGE_CUSTOM_WIDGET,
        isSystem: true,
        fieldDefinitions: [
            {
                name: "title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "cardWidth",
                displayName: "card width",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "100%", value: "100%" },
                    { displayName: "50%", value: "50%" },
                    { displayName: "33%", value: "33%" },
                    { displayName: "25%", value: "25%" },
                ],
                defaultValue: "25%",
                hideEmptyOption: true,
            },
            {
                name: "counters",
                editorTemplate: "ListField",
                getDisplay: ({ fields: { heading } }) => heading || "Counter",
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "heading",
                        editorTemplate: "IntegerField",
                        defaultValue: null,
                        isRequired: true,
                    },
                    {
                        name: "subHeading",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
